<template>
  <div ref="rootRef" class="tg4-image">
    <div :class="['tg4-image-frame', props.aspect, state === 'error' ? 'has-error' : '', largeFallback ? 'large-fallback' : '']">
      <img
        v-if="imageUrl && state !== 'error'"
        class="tg4-image-asset lazy"
        :class="{ 'is-loaded': state === 'loaded' }"
        :data-src="imageUrl"
        :alt="altText"
        @load="onLoad"
        @error="onError"
      />
      <div v-if="badge" class="tg4-image-tag">{{ $t(badgeKey) }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">


import { ref, computed, watch } from 'vue';
import { ImageResponse, SeriesBadge } from '@/types/cms';
import { Language } from '@/enums/language';
import { useStore } from '@/composables/useStore';

type ImageAssetSize = 'thumbnail' | 'large' | 'xLarge' | 'original';

type ImageState = 'loading' | 'loaded' | 'error';

export type ImageAspect = 'landscape' | 'landscape_fill' | 'portrait' | 'square' | 'banner' | 'banner_thin';

const LARGE_FALLBACK_ABOVE = 400;

const props = withDefaults(defineProps<{
  image: ImageResponse | null; 
  aspect?: ImageAspect;
  size?: ImageAssetSize;
  badge?: SeriesBadge | null;
  alt?: string;
}>(), {
  aspect: 'landscape',
  size: 'large',
  badge: null,
  alt: '',
});

const { useGetter } = useStore();
const currentLanguage = useGetter<string>('currentLanguage');

const rootRef = ref<HTMLElement | null>(null);
const loadState = ref<ImageState>('loading');
const largeFallback = ref(false);

const imageUrl = computed(() => props.image?.[props.size] ?? null);

const state = computed<ImageState>(() => (imageUrl.value ? loadState.value : 'error'));

const badgeKey = computed(() => `badge.${props.badge}`);

const altText = computed(() => {
  const img = props.image;
  if (!img) return props.alt;
  const localised = currentLanguage.value === Language.Irish ? img.altTextGa : img.altTextEn;
  return localised ?? img.altTextEn ?? props.alt;
});

const onLoad = () => {
  loadState.value = 'loaded';
};

const onError = () => {
  //Measure width to pick error image
  const width = rootRef.value?.clientWidth ?? 0;
  largeFallback.value = width > LARGE_FALLBACK_ABOVE;
  loadState.value = 'error';
};


watch(imageUrl, () => {
  loadState.value = 'loading';
  largeFallback.value = false;
});
</script>

<style scoped lang="scss">


.tg4-image {
  position: relative;
}

.tg4-image-frame {
  position: relative;
  height: 0;
  overflow: hidden;
  background-color: $color-background-grey;
  background-image: url('~@/assets/images/placeholder.png');
  background-repeat: no-repeat;
  background-position: center center;

  //Portrait failure state is still 16:9
  background-size: cover;
}

.landscape_fill {
  height: 100%;
}

.landscape {
  padding-bottom: 56.25%; // 9 / 16
}

.portrait {
  padding-bottom: 140%; // 7 / 5
}

.square {
  padding-bottom: 100%;
}

.banner {
  padding-bottom: 28.125%; // 9 / 32
}

.banner_thin {
  padding-bottom: 14.0625%; // 9 / 64
}

.has-error {
  background-image: url('~@/assets/images/tg4FallbackImageSmall.png');

  &.large-fallback {
    background-image: url('~@/assets/images/tg4FallbackImage.png');
  }
}

.tg4-image-asset {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;

  // Hidden until it has actually decoded, so the placeholder behind it shows
  // through instead of a half-painted image.
  opacity: 0;
  transition: opacity 0.2s ease-in-out;

  &.is-loaded {
    opacity: 1;
  }
}

.tg4-image-tag {
  position: absolute;
  top: 0;
  left: 0;
  padding: 6px 12px;
  background-color: $color-off-white;
  color: $color-background-grey;
  @include text-cell-title;
}
</style>
